/*
 * Copyright (c) 2025 CalAmp Corp.  All Rights Reserved
 */
import {useCallback, useEffect, useMemo} from 'react';
import {useQuery, useQueryClient} from 'react-query';
import {error} from '@ion/core/src/logger/logger';
import {getDeviceExtendedAttributesById} from '@ion/core/src/services/Connect';
import {ExtendedAttribute} from '@ion/core/src/types/entities';
import {createKey, QueryKey} from '../config/QueryKeys';
import {createQueryResult} from '../utils/helpers';
import {UseDefaultQueryResult} from './types';

export type UseGetDeviceExtAttribsByIdResult = UseDefaultQueryResult & {
  result: ExtendedAttribute;
  refresh: Function;
};

export interface UseGetDeviceExtAttribsByIdParams {
  id: number,
  appKey: string
};

/**
 * The `useGetDeviceExtAttribsById` hook retrieves the device extended attributes
 * details for the given device id.
 * 
 * ```typescript
 *
 * import {useGetDeviceExtAttribsById} from '@ion/query/src/hooks/useGetDeviceExtAttribsById';
 *
 * const {result} = useGetDeviceExtAttribsById(id, appKey);
 * 
 * ```
 *
 * @param id        - The ID of the device to retrieve the extended attributes details for
 * @param enabled   - Whether the query should run automatically.
 * @param staleTime - The duration (in milliseconds) for which the data is
 *                    considered "fresh".
 * @returns         - An object containing the fetched results, a refresh
 *                    function, and query status
 */
export const useGetDeviceExtAttribsById = (
  {
    id,
    appKey
  }: UseGetDeviceExtAttribsByIdParams,
  enabled = true,
  staleTime = 0
): UseGetDeviceExtAttribsByIdResult => {
  const queryClient = useQueryClient();
  const key         = useMemo(() => (createKey(QueryKey.DEVICE_EXTENDED_ATTRIBUTES, id)), [id]);

  const query = useQuery(key, async() => {
    try {
      const results = await getDeviceExtendedAttributesById(id, appKey);
      return results;
    } catch (err) {
      error(err);
      return err;
    }
  }, {
    staleTime : staleTime as number,
    enabled
  });

  useEffect(() => {
    if (!query.data && id && !query.isError) {
      query.refetch();
    }
  }, [query, id]);

  const refresh = useCallback(() => {
    queryClient.invalidateQueries(key);
  }, [id]);

  return createQueryResult(query, {
    result : query.data || {},
    refresh
  }) as UseGetDeviceExtAttribsByIdResult;
};

export default useGetDeviceExtAttribsById;
